import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { PhotoSection } from '../shared/photo';

type GuidePhase = 'pending' | PhotoSection | 'celebration-content' | 'memories-content' | 'done';
type GuideName = 'album' | 'party-album' | 'submission' | 'story';
const seenThisVisit = new Set<GuideName>();

export function hasSeenGuide(name: GuideName) {
  if (seenThisVisit.has(name)) return true;
  try {
    return localStorage.getItem(`lloyds-guide-v1-${name}`) === 'done';
  } catch {
    return false;
  }
}

export function rememberGuide(name: GuideName) {
  seenThisVisit.add(name);
  try {
    localStorage.setItem(`lloyds-guide-v1-${name}`, 'done');
  } catch {
    // Private browsing can prevent storage; still respect dismissal this visit.
  }
}

export function useAlbumGuide(enabled: boolean, ready = true, partyLive = false) {
  const [phase, setPhase] = useState<GuidePhase>(() => (hasSeenGuide('party-album') ? 'done' : 'pending'));
  const [selectedTab, setSelectedTab] = useState<PhotoSection | null>(null);
  const [wasLive, setWasLive] = useState(partyLive);
  // A pre-party replay must never consume the guest's first party guide.
  // Keep completion across later pause/resume cycles and page reloads.
  if (partyLive !== wasLive) {
    setWasLive(partyLive);
    if (partyLive && !hasSeenGuide('party-album')) {
      setPhase('pending');
      setSelectedTab(null);
    }
  }
  useEffect(() => {
    if (!enabled || !ready || !selectedTab) return;
    // Let the 220ms yellow selection slide finish, then hold it in view.
    const timer = window.setTimeout(() => {
      setPhase(`${selectedTab}-content`);
      setSelectedTab(null);
    }, 750);
    return () => window.clearTimeout(timer);
  }, [enabled, ready, selectedTab]);
  useEffect(() => {
    if (!enabled || !ready || phase !== 'pending') return;
    let cancelled = false;
    let timer = 0;
    const begin = async () => {
      await Promise.all([
        document.fonts.ready,
        ...Array.from(document.images)
          .filter((image) => image.loading !== 'lazy')
          .map((image) => image.decode().catch(() => undefined)),
      ]);
      if (!cancelled) timer = window.setTimeout(() => setPhase('memories'), 350);
    };
    if (document.readyState === 'complete') void begin();
    else window.addEventListener('load', begin, { once: true });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener('load', begin);
    };
  }, [enabled, ready, phase]);
  const active = phase !== 'done' && phase !== 'pending';
  // One owner keeps the lock continuous between tab and contribution steps.
  // Programmatic guide positioning still works; guest scrolling does not.
  useLayoutEffect(() => {
    if (!enabled || !active) return;
    const root = document.documentElement;
    const alreadyLocked = root.classList.contains('guide-scroll-locked');
    root.classList.add('guide-scroll-locked');
    const preventScroll = (event: Event) => event.preventDefault();
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('input, textarea, select, [contenteditable="true"]')) return;
      const spaceOnControl = event.key === ' ' && target.closest('button, [role="button"]');
      if (
        !spaceOnControl &&
        [
          'ArrowDown',
          'ArrowUp',
          'ArrowLeft',
          'ArrowRight',
          'PageDown',
          'PageUp',
          'Home',
          'End',
          ' ',
        ].includes(event.key)
      )
        event.preventDefault();
    };
    window.addEventListener('wheel', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });
    document.addEventListener('keydown', onKey);
    return () => {
      if (!alreadyLocked) root.classList.remove('guide-scroll-locked');
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventScroll);
      document.removeEventListener('keydown', onKey);
    };
  }, [enabled, active]);
  const finish = useCallback(() => {
    setSelectedTab(null);
    if (partyLive && active) rememberGuide('party-album');
    setPhase('done');
  }, [partyLive, active]);
  const select = useCallback((section: PhotoSection) => {
    if (active) setSelectedTab(section);
  }, [active]);
  const next = useCallback(() => { setSelectedTab(null); setPhase('celebration'); }, []);
  const restart = useCallback(() => { setSelectedTab(null); setPhase('memories'); }, []);
  return { phase, finish, select, next, restart, active };
}
